import type { ChangeEventHandler, HTMLProps } from 'react';
import { forwardRef, useCallback, useId, useState } from 'react';

import { Collapsible } from '@tripleten/design-system/collapsible';
import { AlertCircle } from '@tripleten/icons/solid/alert-circle.tsx';
import { EyeOff } from '@tripleten/icons/solid/eye-off.tsx';
import { Eye } from '@tripleten/icons/solid/eye.tsx';

import { cnInputWrapper } from './input-wrapper.constants.js';
import { cnInput } from './input.constants.js';

import './input.css';

export interface InputProps {
    errorMessage?: string;
    label?: string;
    activeError?: boolean; // to simply put the input in an error state, without a message
}

const validateType = ['email', 'password', 'text'] as const;

export const Input = forwardRef<HTMLInputElement, HTMLProps<HTMLInputElement> & InputProps>((props, outerRef) => {
    const {
        className,
        errorMessage,
        label,
        onChange,
        type,
        activeError,
        hidden,
        name,
        defaultValue,
        id,
        ...restProps
    } = props;
    const fallbackId = useId();
    const inputId = id ?? fallbackId;
    const [value, setValue] = useState<string | number | undefined | readonly string[]>(defaultValue);
    const [isShownPassword, setIsShownPassword] = useState<boolean>(false);

    const isPasswordType = type === 'password';

    const newType = isShownPassword && isPasswordType ? 'text' : type;

    const showPassword = useCallback(() => {
        setIsShownPassword(!isShownPassword);
    }, [isShownPassword]);

    const isError = (Boolean(errorMessage) || Boolean(activeError)) && validateType.includes(newType as 'text');

    const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
        (e) => {
            const input = e.target;

            setValue(input.value);

            if (onChange) {
                onChange(e);
            }
        },
        [onChange],
    );

    return (
        <div className={cnInputWrapper({ hidden, type: newType })}>
            <div className={cnInput('control-wrapper')}>
                <input
                    type={newType}
                    className={cnInput({ type: newType, empty: !value, error: isError }, [className])}
                    ref={outerRef}
                    onChange={handleChange}
                    hidden={hidden}
                    name={name}
                    id={inputId}
                    value={value}
                    {...restProps}
                />
                {label && (
                    <label htmlFor={inputId} className={cnInput('label')}>
                        {label}
                    </label>
                )}
                {isError && <AlertCircle size={24} className={cnInput('error-icon')} />}
                {isPasswordType &&
                    !isError &&
                    (isShownPassword ? (
                        <Eye size={24} onClick={showPassword} className={cnInput('icon-eye')} />
                    ) : (
                        <EyeOff size={24} className={cnInput('icon-eye')} onClick={showPassword} />
                    ))}
            </div>
            <Collapsible open={isError}>
                <Collapsible.Panel>
                    {isError && <div className={cnInput('message', { error: isError })}>{errorMessage}</div>}
                </Collapsible.Panel>
            </Collapsible>
        </div>
    );
});
